// useCurrentFamily — resolves the active family for the signed-in user.
//
// Steps:
//   1. Find the family_members row(s) where user_id = auth.uid()
//   2. Pick the first one (LITE = one family per user)
//   3. Fetch the family, all members, the default family chat thread, the
//      default todo list, the family crest, and the family's org links.
//
// If the user has no family_members row (shouldn't happen because the
// bootstrap trigger creates one), we expose a "needsBootstrap" flag so the
// UI can show a "create your family" prompt.

import { useCallback, useEffect, useState } from "react";
import { supabase, isAuthConfigured } from "./supabase";
import { useSupabaseQuery } from "./dbHooks";

import type { Database } from "@/types/db";

// Source of truth: the generated Database type. Lets future
// migration regens flow through automatically (added in 021 +
// 022 — the 'role' column is now an enum-ish string).
type FamilyRow       = Database["public"]["Tables"]["families"]["Row"];
type MemberRow       = Database["public"]["Tables"]["family_members"]["Row"];
type ChatThreadRow   = Database["public"]["Tables"]["chat_threads"]["Row"];
type TodoListRow     = Database["public"]["Tables"]["todo_lists"]["Row"];
type FamilyCrestRow  = Database["public"]["Tables"]["family_crest"]["Row"];

// Local row type for `family_org_links`. The generated Database type
// still lists `families.org_id` (stale), so we type the link row
// independently rather than reaching for `families.Row`.
type FamilyOrgLinkRow = { org_id: string };

export type CurrentFamily = {
  family:    FamilyRow;
  members:   MemberRow[];
  self:      MemberRow;
  thread:    ChatThreadRow | null;
  list:      TodoListRow | null;
  crest:     FamilyCrestRow | null;
  /**
   * Post-04901 the family's org links live in
   * `family_org_links`, not the dropped `families.org_id` scalar.
   * Most UIs need a single "current" org; consumers should read
   * `orgIds[0] ?? null`. Multi-org UIs can iterate the full list.
   * Empty array means the family has no org links yet.
   *
   * TODO(db.ts): the generated `Database["public"]["Tables"]["families"]`
   * type in `src/types/db.ts` is stale and still declares `org_id: string`
   * on the Row. The live database no longer returns that column
   * (migration 04901 dropped it). The hook reads `family_org_links`
   * via a locally-typed `FamilyOrgLinkRow` rather than the generated
   * type to avoid a cascading regen. Regenerate `db.ts` via the
   * Management API (`GET /v1/projects/{ref}/types/typescript?included_schemas=public`)
   * to remove the stale field. The hook's runtime behavior is
   * correct either way.
   */
  orgIds:    string[];
  /** True when no membership was found and the user needs to bootstrap. */
  needsBootstrap: boolean;
  loading:   boolean;
  error:     string | null;
  refresh:   () => void;
};

export function useCurrentFamily(userId: string | null): CurrentFamily {
  const [selfMemberId, setSelfMemberId] = useState<string | null>(null);
  const [familyId,     setFamilyId]     = useState<string | null>(null);

  const memberships = useSupabaseQuery<MemberRow[]>(async () => {
    if (!isAuthConfigured() || !userId) return { data: null, error: null };
    return await supabase
      .from("family_members")
      .select("*")
      .eq("user_id", userId)
      .limit(1);
  }, [userId]);

  useEffect(() => {
    const list = memberships.data;
    if (list && list.length > 0) {
      setSelfMemberId(list[0]!.id);
      setFamilyId(list[0]!.family_id);
    } else if (memberships.loading === false) {
      setSelfMemberId(null);
      setFamilyId(null);
    }
  }, [memberships.data, memberships.loading]);

  const family = useSupabaseQuery<FamilyRow>(async () => {
    if (!familyId) return { data: null, error: null };
    return await supabase.from("families").select("*").eq("id", familyId).maybeSingle();
  }, [familyId]);

  const members = useSupabaseQuery<MemberRow[]>(async () => {
    if (!familyId) return { data: null, error: null };
    return await supabase
      .from("family_members")
      .select("*")
      .eq("family_id", familyId)
      .order("display_name", { ascending: true });
  }, [familyId]);

  const thread = useSupabaseQuery<ChatThreadRow | null>(async () => {
    if (!familyId) return { data: null, error: null };
    return await supabase
      .from("chat_threads")
      .select("*")
      .eq("family_id", familyId)
      .eq("kind", "family")
      .maybeSingle();
  }, [familyId]);

  const list = useSupabaseQuery<TodoListRow | null>(async () => {
    if (!familyId) return { data: null, error: null };
    return await supabase
      .from("todo_lists")
      .select("*")
      .eq("family_id", familyId)
      .eq("is_default", true)
      .maybeSingle();
  }, [familyId]);

  const crest = useSupabaseQuery<FamilyCrestRow | null>(async () => {
    if (!familyId) return { data: null, error: null };
    return await supabase
      .from("family_crest")
      .select("*")
      .eq("family_id", familyId)
      .maybeSingle();
  }, [familyId]);

  // Post-04901: family's org links live in `family_org_links`, not
  // the dropped `families.org_id` scalar. The hook exposes the full
  // list so multi-org UIs can iterate; single-org consumers should
  // read `orgIds[0] ?? null`.
  const orgIds = useSupabaseQuery<FamilyOrgLinkRow[]>(async () => {
    if (!familyId) return { data: null, error: null };
    return await supabase
      .from("family_org_links")
      .select("org_id")
      .eq("family_id", familyId);
  }, [familyId]);

  const refresh = useCallback(() => {
    memberships.refresh();
    family.refresh();
    members.refresh();
    thread.refresh();
    list.refresh();
    crest.refresh();
    orgIds.refresh();
  }, [memberships, family, members, thread, list, crest, orgIds]);

  // The placeholder shape returned before data arrives.
  const placeholder: CurrentFamily = {
    family: null as unknown as FamilyRow,
    members: [],
    self:    null as unknown as MemberRow,
    thread:  null,
    list:    null,
    crest:   null,
    orgIds:  [],
    needsBootstrap: false,
    loading: true,
    error: null,
    refresh,
  };

  if (!userId) {
    return { ...placeholder, needsBootstrap: true, loading: false };
  }
  if (memberships.loading || memberships.data === null) {
    return { ...placeholder,
      needsBootstrap: !memberships.loading && (memberships.data?.length ?? 0) === 0,
      error: memberships.error,
    };
  }
  if (memberships.data.length === 0) {
    return { ...placeholder, needsBootstrap: true, loading: false };
  }

  const self = memberships.data[0]!;
  // While family/members/... are still loading, keep the self in place.
  if (family.data === null || members.data === null) {
    return { ...placeholder,
      self,
      loading: true,
      error: family.error ?? members.error ?? null,
    };
  }

  const orgIdList: string[] = (orgIds.data ?? []).map((r: FamilyOrgLinkRow) => r.org_id);
  return {
    family:   family.data!,
    members:  members.data!,
    self,
    thread:   thread.data,
    list:     list.data,
    crest:    crest.data,
    orgIds:   orgIdList,
    needsBootstrap: false,
    loading:  thread.loading || list.loading || crest.loading || orgIds.loading,
    error:    thread.error ?? list.error ?? crest.error ?? orgIds.error ?? null,
    refresh,
  };
}
